import React, { useState, useEffect, useCallback, useMemo } from "react";
import Cookie from "js-cookie";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";
import urls from "../../utils/url";
import "./NewPatientReportToolbar.scss";

// This toolbar (Patient Type/Zone/District/Facility/Date Range) is
// intentionally self-contained: it owns all of its own selection state and
// never touches report.jsx's legacy `filters`/`selectedOptions`/`value1`/
// `value2`. It only ever hands report.jsx resolved values through onApply,
// on Apply/Reset — never on every dropdown change, since the Figma design
// has explicit Apply/Reset buttons rather than an apply-as-you-go UI.
const premuimTabOptions = {
  "All Patients": "all_patient",
  "Intrapartum Tab": "intra_partum_process",
  "Postpartum Tab": "post_partum_process",
  "Completed Tab": "completed",
};

const basicTabOptions = {
  "All Patients": "all_patient",
  "Live Patients": "intra_partum_process",
  Completed: "completed",
};

// "Custom" is handled separately (opens the calendar popover) — not a plain
// preset. "Till Date" is the default/unfiltered state, matching Figma.
const DATE_PRESETS = [
  { key: "day", label: "Today" },
  { key: "week", label: "7 Days" },
  { key: "15days", label: "15 Days" },
  { key: "last30days", label: "30 Days" },
  { key: "month", label: "Current Month" },
  { key: "till_date", label: "Till Date" },
];

const getDateRange = (presetKey) => {
  const today = dayjs();
  if (presetKey === "day")
    return { start: today.startOf("day"), end: today.endOf("day") };
  if (presetKey === "week")
    return {
      start: today.subtract(6, "day").startOf("day"),
      end: today.endOf("day"),
    };
  if (presetKey === "15days")
    return {
      start: today.subtract(14, "day").startOf("day"),
      end: today.endOf("day"),
    };
  if (presetKey === "last30days")
    return {
      start: today.subtract(29, "day").startOf("day"),
      end: today.endOf("day"),
    };
  if (presetKey === "month")
    return { start: today.startOf("month"), end: today.endOf("day") };
  // "till_date" (and any unknown key) — no lower bound.
  return { start: null, end: null };
};

const getStartDay = (date) => {
  if (!date?.$d) return null;
  return dayjs(date.$d).startOf("day");
};

const getEndDay = (date) => {
  if (!date?.$d) return null;
  return dayjs(date.$d).endOf("day");
};

// Same custom day-cell renderer used by UltraAdminAnalytics/BasicAnalytics —
// highlights the in-progress range while picking start/end in one calendar.
const CustomDay = ({ startDate, endDate, day, ...props }) => {
  const isStart = startDate && day.isSame(startDate, "day");
  const isEnd = endDate && day.isSame(endDate, "day");
  const isInRange =
    startDate &&
    endDate &&
    day.isAfter(startDate, "day") &&
    day.isBefore(endDate, "day");

  return (
    <PickersDay
      {...props}
      day={day}
      sx={{
        ...(isInRange && {
          backgroundColor: "#F6F2FF !important",
          borderRadius: "0 !important",
          color: "rgba(108, 23, 217, 1) !important",
        }),
        ...((isStart || isEnd) && {
          backgroundColor: "rgba(232, 212, 255, 1) !important",
          color: "#1a1a1a  !important",
          borderRadius: "50% !important",
        }),
      }}
    />
  );
};

// Truncates long values (long facility/district/zone names) with an
// ellipsis instead of growing the select and pushing Date Range onto a
// second row — same pattern as BasicAnalytics/UltraAdminAnalytics.
const SelectValue = ({ text }) => (
  <span className="new-select-value" title={text}>
    {text}
  </span>
);

const NewPatientReportToolbar = ({ patientTab, accountType, cookie, onApply }) => {
  // Staged like Zone/District/Facility/Date Range — only reaches report.jsx
  // (via onApply) on Apply/Reset, never on change.
  const [stagedPatientTab, setStagedPatientTab] = useState(patientTab);

  const isUltraAdmin =
    JSON.parse(Cookie.get("data") || "{}").is_ultra_admin === true;
  const childPlaces = useMemo(() => {
    try {
      return JSON.parse(Cookie.get("child_places") || "[]");
    } catch (e) {
      return [];
    }
  }, []);

  const [ultraAdminZones, setUltraAdminZones] = useState([]);
  const [facilitiesFromApi, setFacilitiesFromApi] = useState([]);
  // facility-overview's top-level "whole org" place id — one zone in the
  // response legitimately equals this (a SuperAdmin's single zone IS their
  // parent place). Only used to drop a redundant pseudo-zone from the ZONE
  // dropdown's own options when there's more than one real zone to choose
  // from — never to decide whether ultraAdminZones has usable data.
  const [parentPlaceId, setParentPlaceId] = useState(null);
  const isSuperAdmin =
    !isUltraAdmin && (childPlaces.length > 1 || facilitiesFromApi.length > 1);

  // Staged selections — only handed to report.jsx (via onApply) on Apply/Reset.
  const [selectedZone, setSelectedZone] = useState("all");
  const [selectedDistrict, setSelectedDistrict] = useState("all");
  const [selectedFacility, setSelectedFacility] = useState("all");
  const [selectedDatePreset, setSelectedDatePreset] = useState("till_date");
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [isSelectingStartDate, setIsSelectingStartDate] = useState(true);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  

  const zoneOptions = useMemo(() => {
    const zones =
      ultraAdminZones.length > 1
        ? ultraAdminZones.filter((z) => z.zone_place_id !== parentPlaceId)
        : ultraAdminZones;
    return zones.map((z) => ({ id: z.zone_place_id, name: z.zone_place_name }));
  }, [ultraAdminZones, parentPlaceId]);

  // Which bucket (ultraAdminZones vs facilitiesFromApi) actually has data —
  // NOT the isUltraAdmin cookie flag. facility-overview v2 can come back
  // zones-shaped for a SuperAdmin/Admin too (no fixed rule tied to account
  // tier), so branching on the cookie instead of the real response shape
  // left these dropdowns reading an empty bucket whenever the two disagreed.
  const hasZoneData = ultraAdminZones.length > 0;

  // Match BasicAnalytics.jsx filter visibility:
  // - UltraAdmin → Zone + District + Facility (zone gated separately below)
  // - SuperAdmin → District + Facility only
  // - Admin (single facility) → no location filters
  const showDistrictFacilityFilters = isUltraAdmin
    ? hasZoneData
    : isSuperAdmin;

  const districtOptions = useMemo(() => {
    if (hasZoneData) {
      const relevantZones =
        selectedZone === "all"
          ? ultraAdminZones
          : ultraAdminZones.filter((z) => z.zone_place_id === selectedZone);
      const allFacilities = relevantZones.flatMap((z) => z.facilities || []);
      return [
        ...new Set(allFacilities.map((f) => f.district).filter(Boolean)),
      ];
    }
    return [
      ...new Set(facilitiesFromApi.map((f) => f.district).filter(Boolean)),
    ];
  }, [hasZoneData, ultraAdminZones, selectedZone, facilitiesFromApi]);

  const facilityOptions = useMemo(() => {
    if (hasZoneData) {
      const relevantZones =
        selectedZone === "all"
          ? ultraAdminZones
          : ultraAdminZones.filter((z) => z.zone_place_id === selectedZone);
      const allFacilities = relevantZones.flatMap((z) => z.facilities || []);
      const filtered =
        selectedDistrict === "all"
          ? allFacilities
          : allFacilities.filter((f) => f.district === selectedDistrict);
      return filtered.map((f) => ({ id: f.place_id, name: f.place_name }));
    }
    const filtered =
      selectedDistrict === "all"
        ? facilitiesFromApi
        : facilitiesFromApi.filter((f) => f.district === selectedDistrict);
    return filtered.map((f) => ({ id: f.id, name: f.name }));
  }, [
    hasZoneData,
    ultraAdminZones,
    selectedZone,
    selectedDistrict,
    facilitiesFromApi,
  ]);

  const resolvePlaceIds = (zone, district, facility, zones, facilities) => {
    if (zones.length > 0) {
      const relevantZones =
        zone === "all" ? zones : zones.filter((z) => z.zone_place_id === zone);
      const allFacilities = relevantZones.flatMap((z) => z.facilities || []);
      const byDistrict =
        district === "all"
          ? allFacilities
          : allFacilities.filter((f) => f.district === district);
      const finalFiltered =
        facility === "all"
          ? byDistrict
          : byDistrict.filter((f) => f.place_id === facility);
      return finalFiltered.map((f) => f.place_id).toString();
    }
    const byDistrict =
      district === "all"
        ? facilities
        : facilities.filter((f) => f.district === district);
    const finalFiltered =
      facility === "all"
        ? byDistrict
        : byDistrict.filter((f) => f.id === facility);
    return finalFiltered.map((f) => f.id).toString();
  };

  const resolveDateRange = (preset, start, end) => {
    if (preset === "custom") return { start, end };
    return getDateRange(preset);
  };

  const applyFilters = useCallback(
    ({
      zone = selectedZone,
      district = selectedDistrict,
      facility = selectedFacility,
      datePreset = selectedDatePreset,
      rangeStart = customStart,
      rangeEnd = customEnd,
      zones = ultraAdminZones,
      facilities = facilitiesFromApi,
      patientTab: applyPatientTab = stagedPatientTab,
    } = {}) => {
      const placeIds = resolvePlaceIds(zone, district, facility, zones, facilities);
      const range = resolveDateRange(datePreset, rangeStart, rangeEnd);
      const isFilteredNow =
        zone !== "all" ||
        district !== "all" ||
        facility !== "all" ||
        datePreset !== "till_date";

      onApply({
        placeIds,
        fromValue: range.start ? range.start.unix() : "",
        toValue: range.end ? range.end.unix() : "",
        isFiltered: isFilteredNow,
        patientTab: applyPatientTab,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isUltraAdmin,
      selectedZone,
      selectedDistrict,
      selectedFacility,
      selectedDatePreset,
      customStart,
      customEnd,
      ultraAdminZones,
      facilitiesFromApi,
      stagedPatientTab,
      onApply,
    ],
  );

  const fetchFacilityHierarchy = useCallback(async () => {
    try {
      // Matches BasicAnalytics.jsx's fetchAnalytics("facility-overview", {},
      // "v2") call byte-for-byte (fetch + Bearer header, no extra headers) —
      // that call is proven to work for SuperAdmin/Admin on this same
      // endpoint, so any divergence here (axios vs fetch, extra headers)
      // was a plausible source of this data just not coming back.
      const res = await fetch(
        `${urls.DEV_URL}api/analytics/v2/facility-overview/?`,
        { headers: { Authorization: `Bearer ${cookie}` } },
      );
      if (!res.ok) {
        throw new Error(`Request failed (${res.status}) for facility-overview`);
      }
      const data = await res.json();
      if (Array.isArray(data?.zones)) {
        // Keep every zone the API actually returned — a SuperAdmin with a
        // single zone has that zone legitimately equal to parent_place_id
        // (their one zone IS their org), so filtering it out here emptied
        // ultraAdminZones entirely and left District/Facility with no data
        // to derive from. The parent-place exclusion is applied later, only
        // to the ZONE dropdown's own option list (see zoneOptions).
        setUltraAdminZones(data.zones);
        setParentPlaceId(data.parent_place_id ?? null);
      } else {
        const facilities = (data?.results || []).map((item) => ({
          id: item.place_id,
          name: item.place_name || "",
          district: item.district || "",
        }));
        setFacilitiesFromApi(facilities);
        if (facilities.length === 0) {
          // Neither `zones` nor `results` had anything usable — surfaces the
          // actual response shape so a genuine backend/permission mismatch
          // (vs. a legitimately empty account) is visible in the console
          // instead of silently leaving District/Facility empty.
          console.warn(
            "NewPatientReportToolbar: facility-overview returned no zones or results",
            data,
          );
        }
      }
    } catch (err) {
      console.log("Facility hierarchy fetch failed, ignoring", err);
    }
    // Only run once on mount — applyFilters itself changes identity whenever
    // selection state changes, and re-running this fetch on every filter
    // change would refetch the whole hierarchy for no reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookie]);

  useEffect(() => {
    fetchFacilityHierarchy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleZoneChange = (value) => {
    setSelectedZone(value);
    setSelectedDistrict("all");
    setSelectedFacility("all");
  };

  // Picking a district directly (while Zone is still "all") back-fills Zone
  // to the district's owning zone — matches UltraAdminAnalytics.jsx's
  // handleDistrictChange. Only meaningful when there's zone data at all.
  const handleDistrictChange = (value) => {
    if (value !== "all" && hasZoneData) {
      const ownerZone = ultraAdminZones.find((z) =>
        (z.facilities || []).some((f) => f.district === value),
      );
      if (ownerZone) setSelectedZone(ownerZone.zone_place_id);
    }
    setSelectedDistrict(value);
    setSelectedFacility("all");
  };

  // Picking a facility directly back-fills Zone/District to the facility's
  // actual zone/district — matches UltraAdminAnalytics.jsx's/
  // BasicAnalytics.jsx's equivalent back-fill, so the hierarchy path stays
  // visually consistent no matter which dropdown was used to get there.
  const handleFacilityChange = (facilityId) => {
    if (facilityId === "all") {
      setSelectedFacility("all");
      return;
    }
    if (hasZoneData) {
      const ownerZone = ultraAdminZones.find((z) =>
        (z.facilities || []).some((f) => f.place_id === facilityId),
      );
      const facility = ownerZone?.facilities.find(
        (f) => f.place_id === facilityId,
      );
      if (ownerZone) setSelectedZone(ownerZone.zone_place_id);
      if (facility?.district) setSelectedDistrict(facility.district);
    } else {
      const facility = facilitiesFromApi.find((f) => f.id === facilityId);
      if (facility?.district) setSelectedDistrict(facility.district);
    }
    setSelectedFacility(facilityId);
  };

  const handleDateCalendarOpen = (event) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handleDateCalendarClose = () => {
    setDateAnchorEl(null);
    setIsSelectingStartDate(true);
  };

  const handleDateSelect = (date) => {
    setSelectedDatePreset("custom");

    if (isSelectingStartDate) {
      setCustomStart(getStartDay(date));
      setCustomEnd(null);
      setIsSelectingStartDate(false);
      return;
    }

    const start = customStart;
    let end = getEndDay(date);
    let newStart = start;
    if (start && end && (end.isAfter(start) || end.isSame(start, "day"))) {
      // normal order — keep as-is
    } else {
      end = getEndDay(start);
      newStart = getStartDay(date);
    }
    setCustomStart(newStart);
    setCustomEnd(end);
    setIsSelectingStartDate(true);
    handleDateCalendarClose();
  };

  const handleApplyClick = () => {
    applyFilters();
  };

  const handleResetClick = () => {
    setSelectedZone("all");
    setSelectedDistrict("all");
    setSelectedFacility("all");
    setSelectedDatePreset("till_date");
    setCustomStart(null);
    setCustomEnd(null);
    setIsSelectingStartDate(true);
    setStagedPatientTab("all_patient");
    applyFilters({
      zone: "all",
      district: "all",
      facility: "all",
      datePreset: "till_date",
      rangeStart: null,
      rangeEnd: null,
      patientTab: "all_patient",
    });
  };

  const customDateLabel =
    customStart && customEnd
      ? `${customStart.format("D MMM YYYY")} - ${customEnd.format("D MMM YYYY")}`
      : customStart
        ? customStart.format("D MMM YYYY")
        : "Custom";

  const tabOptions =
    accountType && ["Basic", "Navam"].includes(accountType)
      ? basicTabOptions
      : premuimTabOptions;

  return (
    <div className="new-search-filter-div">
      <div className="new-toolbar-row">
        <div className="new-filter-field">
          <p className="new-filter-label">PATIENT TYPE</p>
          <Select
            className="new-filter-select"
            value={stagedPatientTab}
            onChange={(e) => setStagedPatientTab(e.target.value)}
            renderValue={(value) => {
              const match = Object.entries(tabOptions).find(
                ([, optionValue]) => optionValue === value,
              );
              return <SelectValue text={match ? match[0] : value} />;
            }}
          >
            {Object.entries(tabOptions).map(([key, value]) => (
              <MenuItem key={value} value={value}>
                {key}
              </MenuItem>
            ))}
          </Select>
        </div>

        {isUltraAdmin && (
          <div className="new-filter-field">
            <p className="new-filter-label">ZONE</p>
            <Select
              className="new-filter-select"
              value={selectedZone}
              onChange={(e) => handleZoneChange(e.target.value)}
              renderValue={(value) => (
                <SelectValue
                  text={
                    value === "all"
                      ? "All"
                      : zoneOptions.find((z) => z.id === value)?.name || value
                  }
                />
              )}
            >
              <MenuItem value="all">All</MenuItem>
              {zoneOptions.map((z) => (
                <MenuItem key={z.id} value={z.id}>
                  {z.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}

       {showDistrictFacilityFilters && (
          <div className="new-filter-field">
            <p className="new-filter-label">DISTRICT</p>
            <Select
              className="new-filter-select"
              value={selectedDistrict}
              onChange={(e) => handleDistrictChange(e.target.value)}
              renderValue={(value) => (
                <SelectValue text={value === "all" ? "All" : value} />
              )}
            >
              <MenuItem value="all">All</MenuItem>
              {districtOptions.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}

        {showDistrictFacilityFilters && (
          <div className="new-filter-field">
            <p className="new-filter-label">FACILITY</p>
            <Select
              className="new-filter-select"
              value={selectedFacility}
              onChange={(e) => handleFacilityChange(e.target.value)}
              renderValue={(value) => (
                <SelectValue
                  text={
                    value === "all"
                      ? "All"
                      : facilityOptions.find((f) => f.id === value)?.name ||
                        value
                  }
                />
              )}
            >
              <MenuItem value="all">All</MenuItem>
              {facilityOptions.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}

        <div className="new-filter-field new-date-range-field">
        <p className="new-filter-label">DATE RANGE</p>
        <div className="new-date-range-row">
          <button
            type="button"
            className={
              selectedDatePreset === "custom"
                ? "new-date-btn new-date-btn-custom active"
                : "new-date-btn new-date-btn-custom"
            }
            onClick={handleDateCalendarOpen}
          >
            <img src="/calendar-icon.svg" alt="" className="new-date-btn-icon" />
              {customDateLabel}
          </button>
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              className={
                selectedDatePreset === preset.key
                  ? "new-date-btn active"
                  : "new-date-btn"
              }
              onClick={() => setSelectedDatePreset(preset.key)}
            >
              {preset.label}
            </button>
          ))}
        </div>
        </div>
      </div>

      <div className="new-filter-actions">
        <button type="button" className="new-apply-btn" onClick={handleApplyClick}>
          Apply
        </button>
        <button type="button" className="new-reset-btn" onClick={handleResetClick}>
          Reset
        </button>
      </div>

      <Popover
        open={Boolean(dateAnchorEl)}
        anchorEl={dateAnchorEl}
        onClose={handleDateCalendarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: "new-calendar-popover" }}
      >
        <div className="new-date-range-picker">
          {customStart && customEnd ? (
            <div className="new-selected-range-display">
              Selected: {customStart.format("D MMM YYYY")} -{" "}
              {customEnd.format("D MMM YYYY")}
            </div>
          ) : null}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              className="new-mui-calendar"
              value={isSelectingStartDate ? customStart : customEnd}
              onChange={handleDateSelect}
              disableFuture
              slots={{ day: CustomDay }}
              slotProps={{
                day: (dayProps) => ({
                  ...dayProps,
                  startDate: customStart,
                  endDate: customEnd,
                }),
              }}
            />
          </LocalizationProvider>
        </div>
      </Popover>
    </div>
  );
};

export default NewPatientReportToolbar;
